import { DateValue } from "@react-types/calendar";
import * as React from "react";
import {
  AriaCalendarCellProps,
  AriaCalendarGridProps,
  AriaCalendarProps,
  mergeProps,
  useCalendar,
  useCalendarCell,
  useCalendarGrid,
  useFocusRing,
  useLocale,
} from "react-aria";
import { CalendarState, useCalendarState } from "react-stately";
import {
  CalendarDate,
  DateDuration,
  createCalendar,
  endOfMonth,
  getWeeksInMonth,
  isSameMonth,
} from "@internationalized/date";
import { Flex } from "../flex";
import { IconButton } from "../button";
import { Text } from "../text";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { range } from "../../utils/array";
import { Color } from "../../utils/theme";

interface CalendarProps extends AriaCalendarProps<DateValue> {
  numberOfMonths?: number;
  color?: Color;
}

export const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  ({ numberOfMonths = 1, color, ...props }, ref) => {
    const { locale } = useLocale();
    const state = useCalendarState({
      ...props,
      locale,
      createCalendar,
      visibleDuration: { months: numberOfMonths },
    });

    const { calendarProps, prevButtonProps, nextButtonProps, title } =
      useCalendar(props, state);

    console.log(state);

    // function onMonthChange(month: string) {
    //   state.setValue(
    //     parseDate(
    //       `${state.value.year}-${month.padStart(2, "0")}-${state.value.day
    //         .toString()
    //         .padStart(2, "0")}`
    //     )
    //   );
    // }

    // function onYearChange(year: string) {
    //   state.setValue(
    //     parseDate(
    //       `${year}-${state.value.month
    //         .toString()
    //         .padStart(2, "0")}-${state.value.day.toString().padStart(2, "0")}`
    //     )
    //   );
    // }

    return (
      <Flex
        data-color={color}
        inline
        gap={"xs"}
        direction={"column"}
        ref={ref}
        className="p-calendar-wrapper"
        {...calendarProps}
      >
        <Flex align={"center"} justify={"space-between"} className="header">
          <IconButton
            variant="outline"
            radius={"full"}
            size={"sm"}
            color={"gray"}
            {...prevButtonProps}
          >
            <HiChevronLeft size={20} />
          </IconButton>

          <Text color="black" size="sm" weight={"regular"} as="h2">
            {title}
          </Text>

          <IconButton
            variant="outline"
            radius="full"
            size={"sm"}
            color="gray"
            {...nextButtonProps}
          >
            <HiChevronRight size={20} />
          </IconButton>
        </Flex>

        {/* <Flex>
          <Select
            onChange={(value) => onMonthChange(value.target.value as string)}
            value={state.value.month.toString()}
            valueKey="value"
            labelKey="label"
            renderValue={(option) => option?.label?.substring?.(0, 3)}
            items={monthsByIndex.map((m, i) => ({
              label: m,
              value: i + 1,
            }))}
            style={{ flex: 1 }}
          ></Select>

          <Select
            value={state.value.year.toString()}
            onChange={(value) => onYearChange(value.target.value as string)}
            style={{ flex: 1 }}
            labelKey="label"
            valueKey="value"
            items={range(1900, 2024)
              .reverse()
              .map((i) => ({ label: i, value: i }))}
          ></Select>
        </Flex> */}

        <Flex gap={"xl"}>
          {range(1, numberOfMonths).map((n) => {
            return (
              <CalendarGrid
                color={color}
                key={n}
                state={state}
                offset={{ months: n - 1 }}
              />
            );
          })}
        </Flex>
      </Flex>
    );
  }
);

type CalendarGridProps = AriaCalendarGridProps & {
  state: CalendarState;
  offset?: DateDuration;
  color?: Color;
};

function CalendarGrid({
  state,
  offset = {},
  color,
  ...props
}: CalendarGridProps) {
  const { locale } = useLocale();
  const startDate = state.visibleRange.start.add(offset);
  const endDate = endOfMonth(startDate);
  const { gridProps, headerProps, weekDays } = useCalendarGrid(
    { startDate, endDate, ...props },
    state
  );

  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

  return (
    <table {...gridProps}>
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, index) => (
            <th key={index}>
              <div className="p-date-cell">
                <Text size={"sm"} variant="light" weight={"regular"}>
                  {day}
                </Text>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex, startDate)
              .map((date, index) =>
                date ? (
                  <CalendarCell
                    color={color}
                    state={state}
                    date={date}
                    key={index}
                    currentMonth={startDate}
                  ></CalendarCell>
                ) : (
                  <td key={index}></td>
                )
              )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type CalendarCellProps = AriaCalendarCellProps & {
  state: CalendarState;
  currentMonth: CalendarDate;
  color?: Color;
};

function CalendarCell({
  state,
  currentMonth,
  color,
  ...props
}: CalendarCellProps) {
  const ref = React.useRef(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isDisabled,
    isUnavailable,
    formattedDate,
  } = useCalendarCell(props, state, ref);

  const { focusProps, isFocusVisible, isFocused } = useFocusRing();
  const isOutsideMonth = !isSameMonth(currentMonth, props.date);

  return (
    <td {...cellProps}>
      <div
        {...mergeProps(buttonProps, focusProps)}
        ref={ref}
        data-hidden={isOutsideMonth}
        data-selected={isSelected}
        data-disabled={isDisabled}
        data-unavailable={isUnavailable}
        data-focused={isFocused}
        data-focus-visible={isFocusVisible}
        className="p-date-cell"
        data-color={color}
      >
        {formattedDate}
      </div>
    </td>
  );
}

Calendar.displayName = "Calendar";
